/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useEffect, useRef, useState } from "react";

interface UseCallElapsedTimerParams {
  startMs: number | null;
  isVisible: boolean;
}

interface UseCallElapsedTimerResult {
  displayValue: string;
  isVisible: boolean;
}

/**
 * Возвращает строку с длительностью звонка в формате mm:ss или hh:mm:ss.
 * Само определение момента старта выполняется в отдельном хуке.
 */
export function useCallElapsedTimer({
  startMs,
  isVisible,
}: UseCallElapsedTimerParams): UseCallElapsedTimerResult {
  // Компонент живёт одну пользовательскую call session, тогда как LiveKit SID
  // и backend anchor могут смениться при hard recovery.
  const sessionStartRef = useRef<number | null>(null);
  const wasVisibleRef = useRef(false);
  if (
    startMs !== null &&
    (sessionStartRef.current === null || startMs < sessionStartRef.current)
  ) {
    sessionStartRef.current = startMs;
  }
  if (isVisible && startMs !== null) wasVisibleRef.current = true;

  const [nowSecond, setNowSecond] = useState<number>(
    Math.floor(Date.now() / 1000),
  );

  useEffect(() => {
    let rafId = 0;
    const tick = (): void => {
      const second = Math.floor(Date.now() / 1000);
      setNowSecond((prev) => (prev === second ? prev : second));
      rafId = window.requestAnimationFrame(tick);
    };
    rafId = window.requestAnimationFrame(tick);
    return (): void => {
      window.cancelAnimationFrame(rafId);
    };
  }, []);

  const stableStartMs = sessionStartRef.current;
  const elapsedSeconds =
    stableStartMs !== null
      ? Math.max(0, nowSecond - Math.floor(stableStartMs / 1000))
      : 0;

  return {
    displayValue: formatElapsedDuration(elapsedSeconds),
    isVisible: isVisible || (startMs === null && wasVisibleRef.current),
  };
}

/**
 * Форматирует длительность: до часа — mm:ss, от часа — hh:mm:ss.
 */
function formatElapsedDuration(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return [hours, minutes, seconds]
      .map((value) => value.toString().padStart(2, "0"))
      .join(":");
  }

  return [minutes, seconds]
    .map((value) => value.toString().padStart(2, "0"))
    .join(":");
}
